import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useLocale } from '@/hooks/useLocale'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react'
import Breadcrumb from '@/components/layout/Breadcrumb'
import LibraryBreadcrumb from '@/components/library/LibraryBreadcrumb'
import EvidenceDisplay from '@/components/library/EvidenceDisplay'
import RelatedTopics from '@/components/library/RelatedTopics'
import SignLanguageButton from '@/components/library/SignLanguageButton'
import Button from '@/components/ui/Button'
import Card from '@/components/ui/Card'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { libraryApi, type Book, type Topic } from '@/services/libraryApiService'
import { mockBooks } from '@/services/mockDataService'
import { getLocalizedValue } from '@sakeena/shared'

// ================= PDF Viewer =================
import { pdfjs, Document, Page } from 'react-pdf'
import 'react-pdf/dist/Page/TextLayer.css'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import { resolveAssetPath, getImageUrl } from '@/utils/basePath'

// Configure PDF.js worker - use static file from public to avoid sendWithPromise null errors
pdfjs.GlobalWorkerOptions.workerSrc = resolveAssetPath('/pdf.worker.min.mjs')

// Helper to normalize book data (handle both camelCase and PascalCase)
const normalizeBook = (book: any): any => ({
  id: book.id || book.Id || '',
  title: book.title || book.Title || '',
  author: book.author || book.Author || '',
  description: book.description || book.Description || '',
  categoryId: book.categoryId || book.CategoryId || '',
  categoryName: book.categoryName || book.CategoryName || '',
  coverImageUrl: book.coverImageUrl || book.CoverImageUrl || book.imageUrl || book.ImageUrl || '',
  pdfUrl: book.pdfUrl || book.PdfUrl || '',
  order: book.order ?? book.Order ?? 0,
  topics: (book.topics || book.Topics || []).map((t: any) => ({
    id: t.id || t.Id || '',
    title: t.title || t.Title || '',
    content: t.content || t.Content || '',
    order: t.order ?? t.Order ?? 0,
  })),
})

// Helper to normalize topic data
const normalizeTopic = (topic: any): any => ({
  id: topic.id || topic.Id || '',
  title: topic.title || topic.Title || '',
  content: topic.content || topic.Content || '',
  bookId: topic.bookId || topic.BookId || '',
  order: topic.order ?? topic.Order ?? 0,
  evidences: topic.evidences || topic.Evidences || [],
  relatedTopics: topic.relatedTopics || topic.RelatedTopics || [],
})

export default function LibraryBookPage() {
  const { t, i18n } = useTranslation()
  const currentLang = i18n.language || 'ar'
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { localizePath } = useLocale()
  const [searchParams] = useSearchParams()

  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null)
  const [book, setBook] = useState<any | null>(null)
  const [currentTopic, setCurrentTopic] = useState<any | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ---- PDF state ----
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [scale, setScale] = useState<number>(1.0)
  const [pdfError, setPdfError] = useState<string | null>(null)

  const handleDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
    setPageNumber(1)
    setPdfError(null)
  }

  const handlePdfError = (err: any) => {
    console.error('PDF load error:', err)
    setPdfError(t('public.library.pdfLoadError', 'حدث خطأ أثناء تحميل ملف PDF'))
  }

  const changePage = (offset: number) => {
    setPageNumber((prev) => {
      if (!numPages) return prev
      const next = prev + offset
      if (next < 1) return 1
      if (next > numPages) return numPages
      return next
    })
  }

  const zoomIn = () => setScale((s) => Math.min(s + 0.2, 2.5))
  const zoomOut = () => setScale((s) => Math.max(s - 0.2, 0.5))

  // Fetch book
  useEffect(() => {
    const fetchBook = async () => {
      if (!id) return

      try {
        setIsLoading(true)
        setError(null)

        const data = await libraryApi.getBook(id, currentLang)
        if (data) {
          const normalized = normalizeBook(data)
          setBook(normalized)
          setPdfError(null)

          // Check if topic is specified in URL
          const topicParam = searchParams.get('topic')
          if (topicParam && normalized.topics?.some((t: any) => t.id === topicParam)) {
            setSelectedTopicId(topicParam)
          } else if (normalized.topics?.length > 0) {
            setSelectedTopicId(normalized.topics[0].id)
          }
        }
      } catch (err) {
        console.error('Failed to fetch book:', err)
        setError(t('public.library.errorLoadBook'))

        const mockBook = mockBooks.find((b) => b.id.toString() === id)
        if (mockBook) {
          setBook(normalizeBook({
            id: mockBook.id.toString(),
            title: mockBook.title,
            author: mockBook.author,
            description: mockBook.description,
            categoryId: mockBook.category.toString(),
            categoryName: mockBook.categoryName,
            coverImageUrl: mockBook.coverImage,
            order: 0,
            pdfUrl: mockBook.pdfUrl,
          }))
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchBook()
  }, [id, t, searchParams, currentLang])

  // Fetch topic
  useEffect(() => {
    const fetchTopic = async () => {
      if (!selectedTopicId) return

      try {
        setIsLoading(true)
        const data = await libraryApi.getTopic(selectedTopicId)
        if (data) {
          setCurrentTopic(normalizeTopic(data))
        }
      } catch (err) {
        console.error('Failed to fetch topic:', err)
        setError(t('public.library.errorLoadTopic'))
      } finally {
        setIsLoading(false)
      }
    }

    fetchTopic()
  }, [selectedTopicId, t])

  if (isLoading && !book) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </div>
    )
  }

  if (error && !book) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-gray-600">{error}</p>
        <button
          onClick={() => navigate(localizePath('/library'))}
          className="mt-4 text-primary-600 hover:underline"
        >
          {t('public.library.backToIndex')}
        </button>
      </div>
    )
  }

  if (!book) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <p className="text-gray-600">{t('public.library.notFound')}</p>
        <button
          onClick={() => navigate(localizePath('/library'))}
          className="mt-4 text-primary-600 hover:underline"
        >
          {t('public.library.backToIndex')}
        </button>
      </div>
    )
  }

  const topics = book.topics || []
  const currentTopicIndex =
    currentTopic && topics.length > 0 ? topics.findIndex((t: any) => t.id === currentTopic.id) : -1
  const previousTopic = currentTopicIndex > 0 ? topics[currentTopicIndex - 1] : null
  const nextTopic =
    currentTopicIndex >= 0 && currentTopicIndex < topics.length - 1
      ? topics[currentTopicIndex + 1]
      : null

  const breadcrumbItems = [
    { label: t('common.home'), to: '/' },
    { label: t('common.library'), to: '/library' },
    {
      label: getLocalizedValue(book.categoryName, currentLang) || '',
      to: book.categoryId ? `/library?category=${book.categoryId}` : '/library',
    },
    { label: getLocalizedValue(book.title, currentLang) },
  ]

  const libraryBreadcrumbItems = [
    { label: getLocalizedValue(book.categoryName, currentLang) || '' },
    { label: getLocalizedValue(book.title, currentLang) },
    ...(currentTopic ? [{ label: getLocalizedValue(currentTopic.title, currentLang) }] : []),
  ]

  return (
    <>
      <Helmet>
        <title>
          {getLocalizedValue(book.title, currentLang)} - {t('common.library')} - Sakeena
        </title>
        <meta
          name="description"
          content={getLocalizedValue(book.description, currentLang) || t('public.library.readBook', { title: getLocalizedValue(book.title, currentLang) })}
        />
      </Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumb items={breadcrumbItems} className="mb-6" />

        {/* Book Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{getLocalizedValue(book.title, currentLang)}</h1>
          {book.author && (
            <p className="text-lg text-gray-600 mb-4">
              {t('public.library.author')}: {getLocalizedValue(book.author, currentLang)}
            </p>
          )}
          <LibraryBreadcrumb items={libraryBreadcrumbItems} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Introduction */}
            {book.description && (
              <Card className="p-6 mb-6 bg-gray-50">
                <p className="text-lg text-gray-800 leading-relaxed">{getLocalizedValue(book.description, currentLang)}</p>
              </Card>
            )}

            {/* PDF Preview */}
            {book.pdfUrl && (
              <Card className="p-4 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xl font-bold text-gray-900">
                    {t('public.library.pdfPreview') || 'معاينة الكتاب (PDF)'}
                  </h2>
                  <a
                    href={book.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 hover:underline"
                  >
                    {t('public.library.openPdfInNewTab') || 'فتح في نافذة جديدة'}
                  </a>
                </div>

                {/* Controls */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  {/* Zoom controls */}
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={zoomOut}
                    >
                      <ZoomOut className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-700 w-16 text-center">
                      {(scale * 100).toFixed(0)}%
                    </span>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={zoomIn}
                    >
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Page controls */}
                  {numPages && numPages > 0 && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => changePage(-1)}
                        disabled={pageNumber <= 1}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                      <span>
                        {pageNumber} / {numPages}
                      </span>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => changePage(1)}
                        disabled={pageNumber >= numPages}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Viewer */}
                <div className="border rounded-lg overflow-auto max-h-[80vh] flex justify-center bg-gray-50 p-4">
                  <Document
                    key={`${book.id}-${currentLang}-${book.pdfUrl || ''}`}
                    file={book.pdfUrl?.startsWith('http') ? book.pdfUrl : getImageUrl(book.pdfUrl || '')}
                    onLoadSuccess={handleDocumentLoadSuccess}
                    onLoadError={handlePdfError}
                    loading={
                      <div className="flex items-center justify-center py-8">
                        <LoadingSpinner />
                      </div>
                    }
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={scale}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                    />
                  </Document>
                </div>

                {pdfError && (
                  <p className="mt-3 text-sm text-red-600 text-center">{pdfError}</p>
                )}
              </Card>
            )}

            {/* Topic Selector */}
            {topics.length > 1 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('public.library.chooseTopic')}
                </label>
                <select
                  value={selectedTopicId || topics[0]?.id || ''}
                  onChange={(e) => setSelectedTopicId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {topics.map((topic: any) => (
                    <option key={topic.id} value={topic.id}>
                      {getLocalizedValue(topic.title, currentLang)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Current Topic Content */}
            {isLoading && currentTopic ? (
              <div className="flex items-center justify-center py-12">
                <LoadingSpinner />
              </div>
            ) : currentTopic ? (
              <>
                <Card className="p-6 mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">
                    {getLocalizedValue(currentTopic.title, currentLang)}
                  </h2>
                  <div className="prose prose-lg max-w-none">
                    <div
                      className="text-gray-800 leading-relaxed text-lg"
                      dangerouslySetInnerHTML={{ __html: getLocalizedValue(currentTopic.content, currentLang) }}
                    />
                  </div>

                  {/* Sign Language Button */}
                  <div className="mt-6">
                    <SignLanguageButton
                      videoUrl={`https://example.com/videos/sign-language/${book.id}-${currentTopic.id}.mp4`}
                    />
                  </div>
                </Card>

                {/* Evidences */}
                {currentTopic.evidences && currentTopic.evidences.length > 0 && (
                  <EvidenceDisplay evidences={currentTopic.evidences} />
                )}
              </>
            ) : (
              <Card className="p-6 mb-6">
                <p className="text-gray-600">{t('public.library.noContent')}</p>
              </Card>
            )}

            {/* Navigation */}
            {(previousTopic || nextTopic) && (
              <div className="flex items-center justify-between gap-4 pt-6 border-t border-gray-200">
                {previousTopic ? (
                  <Button
                    variant="secondary"
                    onClick={() => setSelectedTopicId(previousTopic.id)}
                    className="flex items-center gap-2"
                  >
                    <ChevronRight className="w-4 h-4" />
                    <div className="text-right">
                      <p className="text-xs text-gray-500">
                        {t('public.library.previous')}
                      </p>
                      <p className="font-medium">{getLocalizedValue(previousTopic.title, currentLang)}</p>
                    </div>
                  </Button>
                ) : (
                  <div />
                )}

                {nextTopic && (
                  <Button
                    variant="secondary"
                    onClick={() => setSelectedTopicId(nextTopic.id)}
                    className="flex items-center gap-2"
                  >
                    <div className="text-start">
                      <p className="text-xs text-gray-500">
                        {t('public.library.next')}
                      </p>
                      <p className="font-medium">{getLocalizedValue(nextTopic.title, currentLang)}</p>
                    </div>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            {currentTopic && currentTopic.relatedTopics && currentTopic.relatedTopics.length > 0 ? (
              <RelatedTopics topics={currentTopic.relatedTopics} />
            ) : (
              <Card className="p-4">
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {t('public.library.related')}
                </h3>
                <p className="text-gray-600 text-sm">
                  {t('public.library.noRelated')}
                </p>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
